import React, { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";

import { z } from "zod";
import { useForm } from "react-hook-form";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/uiComponents/components/ui/form";
import { Input } from "@/uiComponents/components/ui/input";

import { useDispatch, useSelector } from "react-redux";

import { RootState } from "@/store/store";
import { setLoading } from "@/store/redux/appSlice";
import { createBranchApi, getBranchApi } from "@/modules/organizationServices";

import { branchSchema } from "@/utils/schemaHelper";
import { BranchError } from "@/enums/errorMessages";
import { addBranch, setBranches } from "@/store/redux/organization/branchSlice";

import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/enums/routes";
import { addBranchProps } from "@/types/organization";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/uiComponents/components/ui/dialog";
import { ErrorHelper } from "@/utils/errorHelper";
import { toastMessage } from "@/utils/toastHelper";
import { ReusableButton } from "@/reusable/reusableButton";

const AddBranch: React.FC<addBranchProps> = ({ onClose, isOpen }) => {
  if (!isOpen) return null;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector((state: RootState) => state.app.loading);
  const userDetails = useSelector(
    (state: RootState) => state.userDetail.userDetail,
  );
  const role = useSelector((state: RootState) => state.userDetail.role);

  const orgId = userDetails?.organizationId;

  const form = useForm<z.infer<typeof branchSchema>>({
    resolver: zodResolver(branchSchema),
    mode: "onChange",
    defaultValues: {
      branchName: "",
    },
  });

  useEffect(() => {
    if (!role.isSuperAdmin && !role.isOrgAdmin) {
      navigate(ROUTES.BRANCHES);
    }
  }, [role]);
  const getBranches = async () => {
    try {
      if (!orgId) return;
      const res = await getBranchApi(orgId);
      const response = res.branches;
      if (response !== undefined) {
        const branches = Array.isArray(response) ? response : [response];
        dispatch(setBranches(branches));
      }
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    }
  };

  const onSubmit = async (values: z.infer<typeof branchSchema>) => {
    if (loading) return;
    dispatch(setLoading(true));

    try {
      if (!orgId) {
        return;
      }

      await createBranchApi(orgId, values, role);
      getBranches();
      onClose();
      const branchItems = {
        id: Date.now(),
        ...values,
      };
      dispatch(addBranch(branchItems));
      toastMessage("success", BranchError.BRANCH_CREATED);
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Branch</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6 mt-5"
          >
            <FormField
              control={form.control}
              name="branchName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>

                  <FormControl>
                    <Input
                      className="bg-white"
                      {...field}
                      placeholder="Enter branch name"
                    />
                  </FormControl>

                  <FormMessage className="text-xs mt-1" />
                </FormItem>
              )}
            />

            <div className="flex justify-center mt-4 mr-2">
              <ReusableButton
                label="Add"
                className=" mt-5 mb-2 "
                loading={loading}
              />
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
export default AddBranch;
